import {
  type FC, memo, type ReactNode,
} from 'react';
import { type MapOutlinePositionInstance } from '../../../../_shared/constants/mapObjects/mapObjectOutline/outlinePositions';
import { type LatLng } from '../../../../_shared/types/latLng';
import { type MapObjectZIndex } from '../mapObject.types';
import { useAddCursorLabel } from '../mapShape/private/useAddCursorLabel';
import { MapObjectContextProvider } from '../private/mapObjectContext';
import { useMapObjectManager } from '../private/useMapObjectManager';
import { type MapCirclePixelInstance } from './mapCircleModel';
import { useMapCircleOutlines } from './useMapCircleOutlines';

export type MapCircleProps = {
  zIndex: MapObjectZIndex;
  circle: MapCirclePixelInstance;
  renderOutline?: (key: string, outline: MapOutlinePositionInstance) => ReactNode;
  renderCircle?: (key: string, circle: MapCirclePixelInstance) => ReactNode;
  renderCenter?: (key: string, center: LatLng) => ReactNode;
  getCursorLabel?: (position: LatLng) => ({ text: string; angle?: number }) | null | undefined;
};

const MapCirclePixelContainer: FC<MapCircleProps> = ({
  circle, zIndex, renderCircle, renderOutline, renderCenter, getCursorLabel,
}) => {

  const manager = useMapObjectManager();

  const outlines = useMapCircleOutlines(circle, {
    predicate: !!renderOutline,
    hideDuringZoomChangeAnimation: circle.scalesWithMapZoom,
  });

  useAddCursorLabel({
    zIndex,
    getCursorLabel,
  });

  return (
    <MapObjectContextProvider value={{ manager, zIndex }}>
      {renderCircle?.(circle.id, circle)}
      {renderCenter?.(`${circle.id}-center`, {
        lat: circle.lat,
        lng: circle.lng,
      })}
      {renderOutline && outlines.map(
        outline => renderOutline(outline.id, outline))
      }
    </MapObjectContextProvider>
  );
};

const pureComponent = memo(MapCirclePixelContainer);
export { pureComponent as MapCirclePixelContainer };
